import {
    type FormPageFragment,
    type PageRuleFragment,
    PageRuleActionType,
    type FormPageRuleInput,
} from 'nutshell-graphql-types';

import type {SelectPickerOption} from './branching-rules';

export function buildNavOptions(
    graphqlPages: FormPageFragment[] | null | undefined,
    activePageIndex: number | null | undefined
): SelectPickerOption[] {
    if (!graphqlPages || activePageIndex === undefined || activePageIndex === null) {
        return [];
    }
    const navOptions: SelectPickerOption[] = [];
    navOptions.push({
        id: PageRuleActionType.CONTINUE,
        label: 'Continue to next page',
    });
    for (const page of graphqlPages) {
        // Don't show the current page in the options
        if (page.position === activePageIndex + 1) {
            continue;
        }
        navOptions.push({
            id: `${PageRuleActionType.PAGE}:${page.id}`,
            label: 'Page ' + page.position,
        });
    }
    navOptions.push({
        id: `${PageRuleActionType.SUBMIT}`,
        label: 'Submit form',
    });
    return navOptions;
}

// Adds the targetPageId to the rules actionType if it exists
// The output matches the format of the SelectPickerOption id
function ruleToNavValue(rule: PageRuleFragment): string | null {
    switch (rule.actionType) {
        case PageRuleActionType.CONTINUE:
            return PageRuleActionType.CONTINUE;
        case PageRuleActionType.SUBMIT:
            return PageRuleActionType.SUBMIT;
        case PageRuleActionType.PAGE:
            return rule.targetPage?.id ? `${PageRuleActionType.PAGE}:${rule.targetPage.id}` : null;
        default:
            return null;
    }
}

/*
 * Maps the rules into a record that can be used to lookup the rule for an answer option
 * Example return value: {enumElementId: 'actionType:targetPageId'}
 */
export function rulesToNavValueByEnumElementId(
    rules: PageRuleFragment[] | undefined
): Record<string, string> {
    const res: Record<string, string> = {};

    for (const rule of rules ?? []) {
        const enumElementId = rule.enumElement?.id;
        if (!enumElementId) {
            continue;
        }

        const navValue = ruleToNavValue(rule);
        if (navValue) {
            res[enumElementId] = navValue;
        }
    }

    return res;
}

// Splits the actionType and targetPageId from the selected rule option id
export function parseRuleOptionId(ruleId: string): FormPageRuleInput {
    if (ruleId.startsWith('PAGE:')) {
        return {
            actionType: PageRuleActionType.PAGE,
            targetPageId: ruleId.slice('PAGE:'.length),
        };
    }
    if (ruleId === PageRuleActionType.SUBMIT) {
        return {actionType: PageRuleActionType.SUBMIT, targetPageId: null};
    }
    return {actionType: PageRuleActionType.CONTINUE, targetPageId: null};
}
